import React, { useEffect } from "react";
import MainContainer from "../../layout/MainContainer";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Box, Typography, Button, Grid, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLazyGetRefundRequestByIdQuery } from "../../services/main";
import { Loader, showError } from "../../constants";
import moment from "moment";
import { REFUND_STATUS } from "../../types/General";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import DemoToolsPanel from "../../components/DemoToolsPanel";

const RefundDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [getRefundById, { isLoading }] = useLazyGetRefundRequestByIdQuery();
  const [refundData, setRefundData] = React.useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    try {
      const response = await getRefundById({ id }).unwrap();
      if (response?.statusCode === 200) {
        // Prefer single-doc response; fall back to paginated { data: [...] } wrapper
        const payload = response?.data;
        const refundDoc = payload?._id
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data[0]
            : null;
        if (refundDoc?._id) {
          setRefundData(refundDoc);
        } else {
          setRefundData(null);
          showError(response?.message || "Refund request not found");
        }
      } else {
        showError(response?.message || "Error fetching details");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Error fetching details");
    }
  };

  const renderStatus = (status: number) => {
    switch (status) {
      case REFUND_STATUS.PENDING:
        return <span style={{ color: "orange", fontWeight: "bold" }}>Pending</span>;
      case REFUND_STATUS.REFUNDED:
        return <span style={{ color: "green", fontWeight: "bold" }}>Refunded</span>;
      default:
        return <span style={{ color: "red", fontWeight: "bold" }}>Cancelled</span>;
    }
  };

  return (
    <MainContainer>
      <Loader isLoad={isLoading} />
      <div className="main_loyout">
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            Refund Request Details
          </Typography>
        </Box>

        {refundData && (
          <Card className="cards" sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Overview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Request ID</Typography>
                    <Typography variant="body1" fontWeight="bold">{refundData._id || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Current Status</Typography>
                    <Typography variant="body1">{renderStatus(refundData.status)}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Full Name</Typography>
                    <Typography variant="body1">{refundData.userDetail?.fullName || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Email Address</Typography>
                    <Typography variant="body1">{refundData.userDetail?.email || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                    <Typography variant="body1">
                      {formatPhoneLtr(
                        refundData.userDetail?.countryCode,
                        refundData.userDetail?.phone
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                  Listing & Financial Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Property Name</Typography>
                    <Typography variant="body1">{refundData.propertyDetail?.name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Refund Amount</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {refundData.refundedAmount ? `${refundData.refundedAmount} KD` : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Refund Reason</Typography>
                    <Typography variant="body1">{refundData.refundStatus || "No reason provided"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                  Audit Trail
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Requested Date</Typography>
                    <Typography variant="body1">
                      {refundData.createdAt ? moment(refundData.createdAt).format("DD/MM/YYYY hh:mm A") : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Refunded Date</Typography>
                    <Typography variant="body1">
                      {refundData.refundedAt ? moment(refundData.refundedAt).format("DD/MM/YYYY hh:mm A") : "Pending"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        )}

        <DemoToolsPanel
          variant="refund"
          entityId={id}
          context={{
            status: refundData?.status,
            refundedAmount: refundData?.refundedAmount,
            propertyId: refundData?.propertyId?._id || refundData?.propertyId,
            userId: refundData?.userId?._id || refundData?.userId,
          }}
          onSuccess={fetchDetails}
        />
      </div>
    </MainContainer>
  );
};

export default RefundDetails;
